import { BaseModernLayouter } from './BaseModernLayouter';
import { LayoutData, SpacingMode } from '../LayoutData';
import { UpVectorMode, AlignmentMode } from '../../field/FieldTypes';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { POI } from '../../poi/POI';

// Import our modern layouters (JS files)
declare const ModernLayouters: any;

export class ModernCircleLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-circle', 
            'Modern Circle Layouter with TestField Logic',
            {
                modernCircleRadius: 5.0, // 🔄 Unified radius for all modern layouters
                modernCircleFixedSpacing: 1.0,
                modernFlip180: false
            },
            '1d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    // ========================================
    // IMPLEMENTATION OF ABSTRACT METHODS
    // ========================================

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.circle;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        const radius = this.getParameter(layoutData, 'modernCircleRadius', 5.0);
        
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernCircleFixedSpacing', 1.0),
            pathType: 'circle',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            radius: radius, // 🔄 Unified radius setting
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernCircleLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'modernCircleRadius', 5.0);
        const spacing = this.getParameter(layoutData, 'modernCircleFixedSpacing', 1.0);
        return radius > 0 && spacing > 0;
    }

    calculateOrientationQuaternion(position: Vector3, index: number, totalCount: number, layoutData: LayoutData): Quaternion {
        const alignmentMode = layoutData.alignmentMode?.value || AlignmentMode.Aligned;
        const angle = (index / totalCount) * Math.PI * 2;
        
        if (alignmentMode === AlignmentMode.Aligned) {
            // Face tangent direction - simplified quaternion creation
            return new Quaternion(0, Math.sin((angle + Math.PI/2) / 2), 0, Math.cos((angle + Math.PI/2) / 2));
        } else {
            // Face center - simplified quaternion creation  
            return new Quaternion(0, Math.sin((angle + Math.PI) / 2), 0, Math.cos((angle + Math.PI) / 2));
        }
    }

    getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'modernCircleFixedSpacing', 1.0);
    }

    calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        const radius = this.getParameter(layoutData, 'modernCircleRadius', 5.0);
        // Calculate spacing based on circle circumference
        const circumference = 2 * Math.PI * radius;
        return circumference / Math.max(1, objectCount);
    }


}